/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeEvent,
  FC,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react'
import { isIOS } from 'react-device-detect'
import Select, {
  ControlProps,
  GroupBase,
  OptionProps,
  PropsValue, // SingleValueProps,
  StylesConfig,
  components,
} from 'react-select'

import { TypeMargin, TypeSize } from '@/core/types'
import { cn } from '@bem-react/classname'
import { AnimatePresence, motion } from 'framer-motion'

import './Select.scss'

const cnSelect = cn('Select')

const UISelect = Select as unknown as FC<any>

export type SelectThemeType = 'default' | 'white'

export interface ISelect {
  options: any[]
  value: any
  optionValue?: string
  optionLabel?: string
  uid?: string
  label?: string
  name?: string
  errors?: string
  placeholder?: string
  className?: string
  classNameInput?: string
  classNameLabel?: string
  required?: boolean
  readonly?: boolean
  disabled?: boolean
  iconLeft?: ReactNode | string
  iconRight?: ReactNode | string
  noArrow?: boolean
  onChange?: (e: any) => void
  handleOnBlur?: (e?: SyntheticEvent) => void
  handleCustomChange?: (
    e?: ChangeEvent<HTMLInputElement> | number | string,
  ) => void
  sizeInput?: TypeSize
  mb?: TypeMargin
  theme?: SelectThemeType
  info?: string
  defaultValue?: PropsValue<any>
  returnValue?: string
  isMulti?: boolean
  isSearchable?: boolean
  isLoading?: boolean
  instanceId?: string
  ControlComponent?: FC<ControlProps<any, boolean, GroupBase<any>>>
  SingleValue?: FC<any>
  CustomOption?: FC<OptionProps<any, boolean, GroupBase<any>>>
  menuIsOpen?: boolean
}

export const CustomSelect: FC<ISelect> = ({
  options,
  value,
  optionValue = 'value',
  optionLabel = 'label',
  uid,
  label,
  // name,
  required,
  errors,
  placeholder,
  // readonly = false,
  className,
  classNameInput,
  classNameLabel,
  disabled,
  iconLeft,
  iconRight,
  // noArrow,
  // sizeInput = 'md',
  mb = 'sm',
  info,
  defaultValue,
  returnValue,
  isMulti,
  isSearchable = true,
  onChange,
  handleCustomChange,
  isLoading,
  instanceId,
  ControlComponent,
  SingleValue,
  CustomOption,
  // menuIsOpen = false,
}) => {
  const [initValue, setInitValue] = useState<any>()

  useEffect(() => {
    if (value && options && options.length && !isMulti) {
      switch (true) {
        case typeof value === 'string' && isNaN(+value):
          {
            const v = options.find(el => el[optionValue] === value)

            setInitValue(v)
          }
          break
        case typeof value === 'number':
          {
            const v = options.find(el => +el[optionValue] === +value)
            setInitValue(v)
          }
          break

        default:
          setInitValue(value)
          break
      }
    }
    setInitValue(value)
  }, [value, options, isMulti, optionValue])

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
        >
          <path
            d='M4.5 6.75L9 11.25L13.5 6.75'
            stroke='white'
            strokeOpacity='0.45'
            stroke-width='1.5'
            stroke-linecap='round'
            stroke-linejoin='round'
          />
        </svg>
      </components.DropdownIndicator>
    )
  }

  const handleOptionValue = (option: any) => {
    return optionValue && option[optionValue]
  }

  const handleOptionLabel = (option: any) => {
    return optionLabel && option[optionLabel]
  }

  const LoadingIndicator = () =>
    // props: LoadingIndicatorProps<unknown>
    {
      return (
        <div
          // {...props}
          className={cnSelect('loader')}
        >
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      )
    }

  const colorStyles: StylesConfig = {
    clearIndicator: () => ({}),
    container: () => ({}),
    control: () => ({}),
    dropdownIndicator: () => ({}),
    group: () => ({}),
    groupHeading: () => ({}),
    indicatorSeparator: () => ({}),
    input: () => ({}),
    loadingIndicator: () => ({}),
    loadingMessage: () => ({}),
    menu: () => ({}),
    menuList: () => ({}),
    menuPortal: () => ({}),
    multiValue: () => ({}),
    multiValueLabel: () => ({}),
    multiValueRemove: () => ({}),
    noOptionsMessage: () => ({}),
    option: () => ({}),
    placeholder: () => ({}),
    singleValue: () => ({}),
    valueContainer: () => ({}),
  }

  return (
    <div
      className={cnSelect(
        {
          xxl: mb === 'xxl',
          xl: mb === 'xl',
          lg: mb === 'lg',
          md: mb === 'md',
          sm: mb === 'sm',
          none: mb === 'none',
        },
        [className],
      )}
    >
      {!!label && (
        <label
          htmlFor={'select-' + uid}
          className={cnSelect(
            'label',
            {
              required,
            },
            [classNameLabel],
          )}
        >
          {label}
        </label>
      )}
      <div className={cnSelect('wrap')}>
        {iconLeft && (
          <div className={cnSelect('icon', { left: true })}>{iconLeft}</div>
        )}

        <UISelect
          defaultValue={defaultValue}
          styles={colorStyles}
          value={initValue}
          options={options}
          placeholder={placeholder}
          isDisabled={disabled}
          getOptionValue={handleOptionValue}
          getOptionLabel={handleOptionLabel}
          onChange={(e: any) => {
            onChange && onChange(e)
            returnValue &&
              handleCustomChange &&
              handleCustomChange(e[returnValue])
          }}
          className={cnSelect(
            {
              ios: isIOS,
              error: info || !!errors,
              multi:
                isMulti && Array.isArray(initValue) && initValue.length > 0,
              disabled: disabled,
            },
            [classNameInput],
          )}
          classNamePrefix={cnSelect('el')}
          components={{
            DropdownIndicator,
            LoadingIndicator,
            Control: ControlComponent || components.Control,
            SingleValue: SingleValue || components.SingleValue,
            Option: CustomOption || components.Option,
          }}
          isMulti={isMulti}
          // menuIsOpen={menuIsOpen}
          // menuIsOpen={true}
          id={'select-' + uid}
          isSearchable={isSearchable}
          isLoading={isLoading}
          instanceId={instanceId}
        />

        {iconRight && !errors && !info && (
          <div
            className={cnSelect('icon', {
              right: true,
            })}
          >
            {iconRight}
          </div>
        )}

        <AnimatePresence>
          {errors && (
            <motion.div
              className={cnSelect('error')}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {/* <Icon name='alert-error' /> */}

              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='14'
                height='14'
                viewBox='0 0 14 14'
                fill='none'
              >
                <g clipPath='url(#clip0_263_983)'>
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M11.809 7C11.809 9.65599 9.65593 11.8091 6.99994 11.8091C4.34396 11.8091 2.19085 9.65599 2.19085 7C2.19085 4.34401 4.34396 2.19091 6.99994 2.19091C9.65593 2.19091 11.809 4.34401 11.809 7ZM6.99994 13.0091C10.3187 13.0091 13.009 10.3187 13.009 7C13.009 3.68127 10.3187 0.990907 6.99994 0.990907C3.68121 0.990907 0.990851 3.68127 0.990851 7C0.990851 10.3187 3.68121 13.0091 6.99994 13.0091ZM6.99994 10.4636C6.66857 10.4636 6.39994 10.195 6.39994 9.86364L6.39994 9.22727C6.39994 8.8959 6.66857 8.62727 6.99994 8.62727C7.33131 8.62727 7.59994 8.8959 7.59994 9.22727L7.59994 9.86364C7.59994 10.195 7.33131 10.4636 6.99994 10.4636ZM6.99994 7.91818C6.66857 7.91818 6.39994 7.64955 6.39994 7.31818L6.39994 4.13636C6.39994 3.80499 6.66857 3.53636 6.99994 3.53636C7.33131 3.53636 7.59994 3.80499 7.59994 4.13636L7.59994 7.31818C7.59994 7.64955 7.33131 7.91818 6.99994 7.91818Z'
                    fill='#C24720'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_263_983'>
                    <rect width='14' height='14' fill='white' />
                  </clipPath>
                </defs>
              </svg>

              {errors}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
